'use client'

import { FormEvent, useEffect, useState } from 'react'
import { object, ref, string } from 'yup'

import Button from 'shared/ui/Button'
import Input from 'shared/ui/form/Input'
import { Modal, ModalTitle } from 'shared/ui/Modal'
import toast from 'shared/ui/utils/toast'
import { getApiErrors } from 'shared/utils/errors'
import { validateField, validateValues } from 'shared/utils/form'
import { isNilOrEmpty } from 'shared/utils/ramda'

import { reportMissingDeliveryArea } from '../api'

import styles from './ReportMissingDeliveryAreaModal.module.scss'

interface initialValuesTypes {
  customerFirstName: string
  customerLastName: string
  customerEmail: string
  postalCode: string
  city: string
}

const initialValues = {
  customerFirstName: '',
  customerLastName: '',
  customerEmail: '',
  postalCode: '',
  city: ''
}

export const formSchema = object().shape({
  customerFirstName: string()
    .required('Imię jest wymagane')
    .min(3, 'Imię zbyt krótkie')
    .matches(/^\D*$/, {
      message: 'Nieprawidłowy format imienia'
    }),
  customerLastName: string()
    .required('Nazwisko jest wymagane')
    .min(3, 'Nazwisko zbyt krótkie')
    .matches(/^[A-Za-zĄĆĘŁŃÓŚŹŻąćęłńóśźż]+(-[A-Za-zĄĆĘŁŃÓŚŹŻąćęłńóśźż]+)*$/, {
      message: 'Nieprawidłowy format nazwiska'
    }),
  customerEmail: string()
    .required('Email jest wymagany')
    .email('Niepoprawny adres email'),
  postalCode: string().test(
    'is-present',
    'Kod pocztowy jest wymagany',
    function (value) {
      const { path, createError } = this
      const phoneNumber = this.resolve(ref(path))

      if (isNilOrEmpty(phoneNumber)) return true
      if (value && !/\d{2}-\d{3}/.test(value)) {
        return createError({ path, message: 'Nieprawidłowy format' })
      }

      return true
    }
  ),
  city: string().required('Miejscowość jest wymagana')
})

const validateFormField = (values: initialValuesTypes) =>
  validateField(formSchema, values)
const validateFormValues = validateValues(formSchema)

interface ReportMissingDeliveryAreaModalProps {
  open: boolean
  onClose: () => void
  onSubmit: (values: initialValuesTypes) => void
  postalCode: string
  city: string
}

const ReportMissingDeliveryAreaModal = ({
  open,
  onClose,
  postalCode,
  city,
  onSubmit
}: ReportMissingDeliveryAreaModalProps) => {
  const [isLoading, setIsLoading] = useState(false)
  const [valid, setValid] = useState(false)
  const [values, setValues] = useState<initialValuesTypes>(initialValues)

  useEffect(() => {
    if (values) {
      validateFormValues(values, setValid)
    }
  }, [values])

  const handleValueChange = (name: string, value: string) => {
    setValues(prev => ({ ...prev, [name]: value }))
  }

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault()

    try {
      setIsLoading(true)
      await reportMissingDeliveryArea({
        ...values,
        town: values.city
      })
      toast.success(
        'Pomyślnie zgłoszono adres. Poinformujemy Cię jeżeli miejscowość będzie dostępna w naszych dostawach.'
      )
      onSubmit(values)
    } catch (err) {
      toast.error(getApiErrors(err))
      console.error(err)
    } finally {
      setIsLoading(false)
    }
  }

  useEffect(() => {
    if (postalCode && postalCode !== values.postalCode) {
      handleValueChange('postalCode', postalCode)
    }
  }, [postalCode])

  useEffect(() => {
    if (city && city !== values.city) {
      handleValueChange('city', city)
    }
  }, [city])

  return (
    <Modal
      open={open}
      onClose={onClose}
      id='report-missing-postal-code-modal'
      size='small'
    >
      <ModalTitle>Zgłoś adres</ModalTitle>
      <form noValidate className={styles.form} onSubmit={handleSubmit}>
        <Input
          label='Imię'
          name='customerFirstName'
          value={values.customerFirstName}
          onChange={handleValueChange}
          validate={validateFormField(values)}
        />
        <Input
          label='Nazwisko'
          name='customerLastName'
          value={values.customerLastName}
          onChange={handleValueChange}
          validate={validateFormField(values)}
        />
        <Input
          label='Email'
          name='customerEmail'
          value={values.customerEmail}
          onChange={handleValueChange}
          validate={validateFormField(values)}
        />
        <Input
          label='Kod pocztowy'
          name='postalCode'
          value={values.postalCode}
          onChange={handleValueChange}
          validate={validateFormField(values)}
        />
        <Input
          label='Miejscowość'
          name='city'
          value={values.city}
          onChange={handleValueChange}
          validate={validateFormField(values)}
        />
        <div className={styles.buttonsWrapper}>
          <Button
            fullWidth
            type='submit'
            variant='primary'
            disabled={!valid}
            loading={isLoading}
          >
            Potwierdź
          </Button>
          <Button fullWidth variant='primaryOutline' onClick={onClose}>
            Anuluj
          </Button>
        </div>
      </form>
    </Modal>
  )
}

export default ReportMissingDeliveryAreaModal
